import { useEffect, useState } from "react";
import { useTenant } from "../contexts/TenantContext";
import { fetchClasses } from "../api/apiClient";
import { fetchInstructorCourses } from "../api/backend";
import { sortByStatus } from "../components/assignClass/classStatus";

const MANAGER_ROLES = ["COURSE_COORDINATOR", "ADMIN", "GLOBAL_ADMIN"];

/** Coordinators and admins look after every class in the tenant, not only the ones they teach. */
export const canManageAllClasses = (roles) => Array.isArray(roles) && roles.some((r) => MANAGER_ROLES.includes(r));

const asList = (data) => (Array.isArray(data) ? data : []);

/** Managers: the tenant's classes. Instructors: the same records, narrowed to the classes they teach. */
async function loadClasses(manager, userId, tenantId) {
  if (manager) return asList(await fetchClasses(tenantId));
  const [taught, all] = await Promise.all([fetchInstructorCourses(userId, tenantId), fetchClasses(tenantId)]);
  const ids = new Set(asList(taught).map((c) => c.id));
  return asList(all).filter((c) => ids.has(c.id));
}

/**
 * Classes the signed-in user may add Knowledge Bank content to, loaded when `open` turns true.
 * Managers get every tenant class that is not completed; instructors get the classes they teach. Rows carry the full
 * class record (schedule, instructor name) and are ordered in progress, then soonest upcoming, then the rest.
 * Returns { courses, loading, error } — a failed load sets `error` rather than showing an empty list.
 */
export default function useAssignableClasses(open) {
  const { effectiveUser } = useTenant() || {};
  const [state, setState] = useState({ courses: [], loading: false, error: null });
  const userId = effectiveUser?.id;
  const tenantId = effectiveUser?.tenantId;
  const manager = canManageAllClasses(effectiveUser?.roles);

  useEffect(() => {
    if (!open || !userId) return undefined;
    let active = true;
    setState({ courses: [], loading: true, error: null });
    loadClasses(manager, userId, tenantId)
      .then((list) => {
        if (active) setState({ courses: sortByStatus(list.filter((c) => c.lifecycleStatus !== "COMPLETED")), loading: false, error: null });
      })
      .catch(() => {
        if (active) setState({ courses: [], loading: false, error: "Could not load classes. Try again." });
      });
    return () => {
      active = false;
    };
  }, [open, userId, tenantId, manager]);

  return state;
}
